import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { setSessionToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  // Proper ref — survives re-renders. Plain `{ current: false }` was being
  // recreated on every render and let iOS Chrome's BFCache restore re-fire
  // the effect, triggering a 2nd exchange of an already-consumed session_id.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    // CRITICAL: strip the session_id from the URL immediately so that if iOS
    // Chrome / Safari restores this tab on next cold-start, it doesn't try to
    // re-consume the same (now invalid) token and surface "Sign-in failed".
    if (window.history?.replaceState) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id: sessionId });
        // Persist the token so the api lib can attach it as Authorization:
        // Bearer on every subsequent request — defends against iOS Chrome /
        // Safari blocking the SameSite=None session cookie.
        if (r.data?.session_token) {
          setSessionToken(r.data.session_token);
        }
        setUser(r.data.user);
        navigate("/dashboard", { replace: true, state: { user: r.data.user } });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        let msg;
        if (status === 403 && detail) msg = detail;
        else if (status === 401) msg = "Sign-in link expired. Please try again.";
        else if (detail) msg = detail;
        else msg = "Sign-in failed. Please try again.";
        navigate("/login", { replace: true, state: { error: msg } });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center" data-testid="auth-callback-loading">
        <div className="h-8 w-8 mx-auto border-2 border-[#0a2540] border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-sm text-slate-500 font-mono-data">Verifying your access…</p>
      </div>
    </div>
  );
}
